import {
  enrollmentCalendarRowToGetEnrollmentResponse,
  parseEnrollmentsCalendarFromWidgetData,
} from "@/lib/dashboard/enrollment-dashboard-widgets";
import type { WidgetDataItemFromApi } from "@/lib/dashboard/widgets-client";
import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";
import { isPendingEnrollment } from "@/lib/utils/enrollment";
import {
  EnrollmentStatus,
  Origin,
  type GetEnrollmentResponse,
} from "@/openapi/generated";

export const PENDING_ENROLLMENTS_REVIEW_WIDGET_KEYS: readonly DashboardWidgetId[] =
  [
    DashboardWidgetId.RecentPendingEnrollments,
    DashboardWidgetId.EnrollmentsCalendar,
  ] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object";
}

function parseFiniteNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseDate(value: unknown): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function splitStudentName(full: string): {
  firstName: string;
  lastName: string;
} {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "Unknown", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function pendingWidgetRowToGetEnrollmentResponse(
  row: Record<string, unknown>,
): GetEnrollmentResponse | null {
  const enrollmentId =
    parseFiniteNumber(row.enrollmentId) ?? parseFiniteNumber(row.id);
  if (enrollmentId == null || enrollmentId <= 0) return null;

  if (parseFiniteNumber(row.classId) != null) {
    const fromCalendar = enrollmentCalendarRowToGetEnrollmentResponse({
      ...row,
      enrollmentId,
      status:
        typeof row.status === "string" && row.status.trim()
          ? row.status
          : EnrollmentStatus.Pending,
    });
    if (fromCalendar) return fromCalendar;
  }

  const studentName =
    typeof row.studentName === "string" && row.studentName.trim()
      ? row.studentName.trim()
      : "Unknown Student";
  const className =
    typeof row.className === "string" && row.className.trim()
      ? row.className.trim()
      : "Unknown Class";
  const enrolledAt =
    parseDate(row.createdAt) ??
    parseDate(row.enrolledAt) ??
    parseDate(row.date);
  const { firstName, lastName } = splitStudentName(studentName);
  const studentId = parseFiniteNumber(row.studentId);
  const classId = parseFiniteNumber(row.classId);

  return {
    enrollmentId,
    enrolledAt,
    status: EnrollmentStatus.Pending,
    updatedAt: enrolledAt ?? new Date(),
    statusChangedAt: null,
    droppedAt: null,
    dropDate: null,
    rejectedAt: null,
    cancelledAt: null,
    completedAt: null,
    origin: Origin.Member,
    processingTaskId: null,
    processingTaskStatus: null,
    activityHistory: [],
    student: {
      studentId: studentId ?? 0,
      firstName,
      lastName,
      email: null,
      isActive: true,
    },
    _class: {
      classId: classId ?? 0,
      name: className,
      description: null,
      isActive: true,
    },
  };
}

export function parseRecentPendingEnrollmentsFromWidgetData(
  dataItems: WidgetDataItemFromApi[],
): GetEnrollmentResponse[] {
  const row = dataItems.find(
    (item) => item.key === DashboardWidgetId.RecentPendingEnrollments,
  );
  if (!row?.data || !isRecord(row.data)) return [];

  const type = row.data.$type;
  if (
    type != null &&
    type !== "pending-enrollments" &&
    type !== DashboardWidgetId.RecentPendingEnrollments
  ) {
    return [];
  }
  if (!Array.isArray(row.data.items)) return [];

  return row.data.items
    .filter(isRecord)
    .map(pendingWidgetRowToGetEnrollmentResponse)
    .filter((item): item is GetEnrollmentResponse => item != null);
}

export function parsePendingCalendarEnrollmentsFromWidgetData(
  dataItems: WidgetDataItemFromApi[],
): GetEnrollmentResponse[] {
  return parseEnrollmentsCalendarFromWidgetData(dataItems).filter(
    (enrollment) =>
      enrollment.status === EnrollmentStatus.Pending ||
      isPendingEnrollment(enrollment),
  );
}

function enrollmentDetailScore(enrollment: GetEnrollmentResponse): number {
  let score = 0;
  if (Number(enrollment.student?.studentId) > 0) score += 2;
  if (Number(enrollment._class?.classId) > 0) score += 2;
  if (enrollment.enrolledAt) score += 1;
  return score;
}

export function enrollmentNeedsReviewLinks(
  enrollment: GetEnrollmentResponse,
): boolean {
  return (
    Number(enrollment.student?.studentId) <= 0 ||
    Number(enrollment._class?.classId) <= 0
  );
}

export function pendingEnrollmentIdsNeedingReviewLinks(
  enrollments: readonly GetEnrollmentResponse[],
): number[] {
  const ids = new Set<number>();
  for (const enrollment of enrollments) {
    if (!enrollmentNeedsReviewLinks(enrollment)) continue;
    const id = Number(enrollment.enrollmentId);
    if (Number.isFinite(id) && id > 0) ids.add(id);
  }
  return Array.from(ids).sort((a, b) => a - b);
}

function enrollmentSortTime(enrollment: GetEnrollmentResponse): number {
  if (enrollment.enrolledAt) {
    const time = new Date(enrollment.enrolledAt).getTime();
    if (Number.isFinite(time)) return time;
  }
  return Number(enrollment.enrollmentId) || 0;
}

/** Studio GET often omits waitlist/pending rows; widgets and GET-by-id do not. */
export function mergePendingEnrollmentsForReview(
  sources: readonly (readonly GetEnrollmentResponse[])[],
): GetEnrollmentResponse[] {
  const byId = new Map<number, GetEnrollmentResponse>();

  for (const source of sources) {
    for (const enrollment of source) {
      const id = Number(enrollment.enrollmentId);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (
        enrollment.status !== EnrollmentStatus.Pending &&
        !isPendingEnrollment(enrollment)
      ) {
        continue;
      }

      const existing = byId.get(id);
      if (
        !existing ||
        enrollmentDetailScore(enrollment) > enrollmentDetailScore(existing)
      ) {
        byId.set(id, enrollment);
      }
    }
  }

  return Array.from(byId.values()).sort(
    (a, b) => enrollmentSortTime(b) - enrollmentSortTime(a),
  );
}

export function parsePendingEnrollmentsReviewFromWidgetData(
  dataItems: WidgetDataItemFromApi[],
): GetEnrollmentResponse[] {
  return mergePendingEnrollmentsForReview([
    parseRecentPendingEnrollmentsFromWidgetData(dataItems),
    parsePendingCalendarEnrollmentsFromWidgetData(dataItems),
  ]);
}
